import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { PartyPopper, Truck, Store, ArrowRight } from "lucide-react";
import { money } from "@/lib/orderStatus";

// Festive "Paiement reçu 🎉" confirmation with order recap, shown right after an online payment.
export default function PaymentSuccess({ order, onContinue }) {
  const { t } = useTranslation();
  const currency = order.currency || "XOF";
  const isPickup = order.payment_type === "on_pickup" || order.mode === "retrait";
  const confetti = ["#E85D04", "#2D6A4F", "#FFBA08", "#1DC8FF", "#7048E8", "#FF7900"];

  return (
    <div className="relative text-center pt-2 pb-1 overflow-hidden" data-testid="payment-success">
      {/* Confetti */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        {confetti.map((c, i) => (
          <motion.span
            key={i}
            initial={{ y: -20, opacity: 0, rotate: 0 }}
            animate={{ y: 220, opacity: [0, 1, 1, 0], rotate: 360 }}
            transition={{ duration: 1.6 + (i % 3) * 0.4, delay: i * 0.08, ease: "easeIn" }}
            className="absolute top-0 h-2.5 w-2.5 rounded-sm"
            style={{ left: `${8 + i * 15}%`, background: c }}
          />
        ))}
      </div>

      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 14 }}
        className="relative z-10 h-20 w-20 mx-auto rounded-full bg-[#2D6A4F] text-white flex items-center justify-center shadow-lg"
      >
        <PartyPopper className="h-9 w-9" />
      </motion.div>

      <motion.h3
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="relative z-10 font-display text-2xl font-extrabold mt-4 text-[#1A1A1A]"
      >
        {t("paySuccess.title")}
      </motion.h3>
      <motion.p
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
        className="relative z-10 text-sm text-[#5C574F] mt-1 px-4"
      >
        {t("paySuccess.sub", { name: order.customer_name || "" })}
      </motion.p>

      <div className="relative z-10 bg-[#F9F6F0] rounded-2xl p-4 mt-5 text-left space-y-2">
        <div className="flex justify-between text-xs text-[#9c968a]">
          <span>{t("paySuccess.orderNo")}</span>
          <span className="font-semibold text-[#5C574F]">#{order.order_id.slice(-6)}</span>
        </div>
        {order.items.map((it, i) => (
          <div key={`${it.product_id}-${it.size || ""}-${i}`} className="flex justify-between text-sm">
            <span className="text-[#5C574F] truncate mr-2">{it.name} ×{it.qty}{it.size ? ` · ${it.size}` : ""}</span>
            <span className="font-medium shrink-0">{money(it.unit_price * it.qty, currency)}</span>
          </div>
        ))}
        {order.delivery_fee > 0 && (
          <div className="flex justify-between text-sm text-[#5C574F]"><span>{t("paySuccess.delivery")}</span><span>{money(order.delivery_fee, currency)}</span></div>
        )}
        <div className="border-t border-[#E5E0D8] pt-2 flex justify-between font-display font-bold text-[#2D6A4F]">
          <span>{t("paySuccess.totalPaid")}</span><span>{money(order.total, currency)}</span>
        </div>
      </div>

      <div className="relative z-10 mt-3 flex items-center justify-center gap-2 text-xs text-[#5C574F] bg-[#E85D04]/8 rounded-xl p-3">
        {isPickup ? <Store className="h-4 w-4 text-[#7048E8] shrink-0" /> : <Truck className="h-4 w-4 text-[#E85D04] shrink-0" />}
        <span>{isPickup ? t("paySuccess.nextPickup") : t("paySuccess.nextDelivery")}</span>
      </div>

      <button
        data-testid="pay-success-track-btn"
        onClick={onContinue}
        className="relative z-10 w-full h-14 mt-5 rounded-2xl bg-[#E85D04] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
      >
        {t("paySuccess.track")} <ArrowRight className="h-5 w-5" />
      </button>
    </div>
  );
}
